// screens/DeepLessonRedirect.tsx — V29 S8.
//
// The persisted `/studia/lezione/deep/:argomentoId` alias (old chat deep-links,
// PRODUCT-VOCABULARY: routes never break) used to mount the retired
// AdaptiveLessonScreen. It now resolves to the argomento's FIRST INCOMPLETE
// guided lesson so an old link still lands in a REAL lesson (F1 disposition:
// never downgrade a lesson link to a list page); the argomento detail is the
// fallback when nothing resolves.

import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTokens } from '../state/theme';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { lezioneApi } from '../api/lezione';
import { guidedLessonRoute } from '../state/lesson-entry';
import { buildCanonicalLessonRows } from '../state/canonical-learning';

export default function DeepLessonRedirect(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { argomentoId } = useParams<{ argomentoId: string }>();

  useEffect(() => {
    if (!argomentoId) { navigate('/progresso', { replace: true }); return; }
    let alive = true;
    void Promise.all([
      lezioneApi.argomentoDetail(argomentoId),
      lezioneApi.lessonProgressSummary().catch(() => null),
    ])
      .then(([detail, progress]) => {
        if (!alive) return;
        const rows = buildCanonicalLessonRows({
          argomentoId,
          microTopics: detail.microTopics,
          readByMicroTopic: {},
          guidedCompleted: false,
          guidedOpened: false,
          progressLessons: progress?.lessons ?? [],
        });
        const target = rows.find((r) => r.state !== 'completed') ?? rows[0] ?? null;
        navigate(
          target
            ? guidedLessonRoute(argomentoId, target.lessonId)
            : `/studia/argomento/${argomentoId}`,
          { replace: true },
        );
      })
      .catch(() => {
        if (alive) navigate(`/studia/argomento/${argomentoId}`, { replace: true });
      });
    return () => { alive = false; };
  }, [argomentoId, navigate]);

  return (
    <Screen>
      <TopChrome title="Lezione" subtitle="Un attimo…" profileChip={false} />
      <main style={{ flex: 1, padding: 16 }}>
        <div aria-busy="true" style={{ height: 120, borderRadius: 14, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
      </main>
    </Screen>
  );
}
